/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { useState } from 'react'
import { X, GitCommit, Loader2, CheckCircle, AlertCircle, Sparkles } from 'lucide-react'
import { commitAndPush, type FileChange } from '@/lib/git-operations'
import { useChatStore } from '@/ui/stores/chat-store'

interface GitCommitModalProps {
  repoUrl: string
  branch: string
  files: FileChange[]
  onClose: () => void
  onSuccess?: () => void
}

export function GitCommitModal({ repoUrl, branch, files, onClose, onSuccess }: GitCommitModalProps) {
  const [commitMessage, setCommitMessage] = useState('')
  const [isCommitting, setIsCommitting] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [progress, setProgress] = useState('')
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null)

  // Get the chat store to use the same model
  const model = useChatStore((state) => state.model)

  const handleAutoGenerate = async () => {
    setIsGenerating(true)
    setResult(null)

    try {
      // Create a summary of changed files with their content preview
      const filesList = files.map(f => {
        const preview = f.content.substring(0, 200)
        return `${f.path}:\n${preview}${f.content.length > 200 ? '...' : ''}`
      }).join('\n\n')

      // Create the prompt for commit message generation
      const userMessage = `Generate a concise, professional git commit message for the following changed files:

${filesList}

Requirements:
- Start with a type prefix: feat:, fix:, docs:, refactor:, style:, test:, chore:
- Keep the first line under 72 characters
- Be specific about what changed
- Use imperative mood ("Add feature" not "Added feature")
- If multiple changes, add bullet points after a blank line

Example format:
feat: Add user authentication system

- Implement JWT token generation
- Add login/logout endpoints
- Create user session management

Generate ONLY the commit message, no explanations or extra text.`

      // Import smartChatCompletion dynamically
      const { smartChatCompletion } = await import('@/providers/llm-provider')

      // Make a standalone API call (don't include chat history to avoid complexity)
      const response = await smartChatCompletion({
        model,
        messages: [{ role: 'user', content: userMessage }],
        maxTokens: 500,
        temperature: 0.7,
      })

      console.log('[GitCommitModal] Auto-generate response:', {
        hasContent: !!response.content,
        contentLength: response.content?.length,
        contentPreview: response.content?.substring(0, 100),
        fullResponse: response,
      })

      if (response.content && response.content.trim()) {
        const generatedMessage = response.content.trim()
        setCommitMessage(generatedMessage)
      } else {
        console.error('[GitCommitModal] No content in response:', response)
        setResult({ success: false, message: 'Failed to generate commit message - no content in response' })
      }
    } catch (error) {
      console.error('[GitCommitModal] Auto-generate error:', error)
      setResult({
        success: false,
        message: `Failed to generate: ${error instanceof Error ? error.message : 'Unknown error'}`
      })
    } finally {
      setIsGenerating(false)
    }
  }

  const handleAction = async (action: 'commit' | 'push' | 'commit-and-push') => {
    if (!commitMessage.trim() && action !== 'push') {
      setResult({ success: false, message: 'Please enter a commit message' })
      return
    }

    setIsCommitting(true)
    setResult(null)

    if (action === 'commit') {
      setProgress('Committing changes...')
      // Commit only (no push)
      const commitResult = await commitAndPush(
        repoUrl,
        branch,
        commitMessage,
        files,
        (msg) => setProgress(msg)
      )
      setIsCommitting(false)
      setResult(commitResult)

      if (commitResult.success) {
        setTimeout(() => {
          onSuccess?.()
          onClose()
        }, 2000)
      }
    } else if (action === 'push') {
      setProgress('Pushing to remote...')
      // Push only (assumes changes are already committed)
      try {
        // const { executeGitCommand } = await import('@/lib/git-operations')
        // TODO: Need to get directory handle to push
        setResult({ success: false, message: 'Push-only not yet implemented' })
      } catch (error) {
        setResult({ success: false, message: `Failed to push: ${error instanceof Error ? error.message : 'Unknown error'}` })
      }
      setIsCommitting(false)
    } else {
      // commit-and-push
      setProgress('Starting commit and push...')
      const commitResult = await commitAndPush(
        repoUrl,
        branch,
        commitMessage,
        files,
        (msg) => setProgress(msg)
      )
      setIsCommitting(false)
      setResult(commitResult)

      if (commitResult.success) {
        setTimeout(() => {
          onSuccess?.()
          onClose()
        }, 2000)
      }
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-background-secondary border border-border rounded-lg w-full max-w-2xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <GitCommit className="w-5 h-5 text-accent" />
            <h2 className="text-lg font-title">Git Operations</h2>
          </div>
          <button onClick={onClose} className="text-foreground-muted hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Repository Info */}
        <div className="mb-4 p-3 bg-background-tertiary rounded border border-border">
          <div className="text-xs text-foreground-muted mb-1">Repository</div>
          <div className="text-sm font-mono">{repoUrl}</div>
          <div className="text-xs text-foreground-muted mt-2">Branch: <span className="text-accent">{branch}</span></div>
          <div className="text-xs text-foreground-muted">Files to commit: <span className="text-accent">{files.length}</span></div>
        </div>

        {/* Files List */}
        <div className="mb-4">
          <div className="text-xs font-semibold text-foreground-secondary mb-2">Changed Files:</div>
          <div className="max-h-32 overflow-y-auto bg-background-tertiary rounded border border-border p-2">
            {files.map((file, idx) => (
              <div key={idx} className="text-xs font-mono text-foreground-muted py-1">
                {file.path}
              </div>
            ))}
          </div>
        </div>

        {/* Commit Message */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-xs font-semibold text-foreground-secondary">
              Commit Message *
            </label>
            <button
              onClick={handleAutoGenerate}
              disabled={isGenerating || isCommitting}
              className="text-xs flex items-center gap-1 px-2 py-1 rounded bg-accent/10 hover:bg-accent/20 text-accent disabled:opacity-50 disabled:cursor-not-allowed"
              title="Auto-generate commit message using AI"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-3 h-3" />
                  Auto-generate
                </>
              )}
            </button>
          </div>
          <textarea
            value={commitMessage}
            onChange={(e) => setCommitMessage(e.target.value)}
            placeholder="Enter a descriptive commit message..."
            className="input w-full min-h-[100px] resize-y"
            disabled={isCommitting || isGenerating}
            autoFocus
          />
        </div>

        {/* Progress */}
        {progress && (
          <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded flex items-center gap-2">
            <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
            <span className="text-xs text-blue-400">{progress}</span>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className={`mb-4 p-3 rounded flex items-center gap-2 ${result.success
            ? 'bg-green-500/10 border border-green-500/30'
            : 'bg-red-500/10 border border-red-500/30'
            }`}>
            {result.success ? (
              <CheckCircle className="w-4 h-4 text-green-400" />
            ) : (
              <AlertCircle className="w-4 h-4 text-red-400" />
            )}
            <span className={`text-xs ${result.success ? 'text-green-400' : 'text-red-400'}`}>
              {result.message}
            </span>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="btn-secondary px-4 py-2"
            disabled={isCommitting}
          >
            Cancel
          </button>

          {/* Commit only */}
          <button
            onClick={() => handleAction('commit')}
            className="bg-background-tertiary border border-border rounded px-4 py-2 text-sm flex items-center gap-2 hover:bg-background-hover disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isCommitting || !commitMessage.trim()}
            title="Commit changes locally without pushing"
          >
            {isCommitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <GitCommit className="w-4 h-4" />
            )}
            Commit
          </button>

          {/* Push only */}
          {repoUrl && (
            <button
              onClick={() => handleAction('push')}
              className="bg-background-tertiary border border-border rounded px-4 py-2 text-sm flex items-center gap-2 hover:bg-background-hover disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isCommitting}
              title="Push already committed changes to remote"
            >
              {isCommitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                </svg>
              )}
              Push
            </button>
          )}

          {/* Commit & Push */}
          {repoUrl && (
            <button
              onClick={() => handleAction('commit-and-push')}
              className="btn-primary px-4 py-2 flex items-center gap-2"
              disabled={isCommitting || !commitMessage.trim()}
              title="Commit and push changes in one action"
            >
              {isCommitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Committing & Pushing...
                </>
              ) : (
                <>
                  <GitCommit className="w-4 h-4" />
                  Commit & Push
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

